import { normalisePhone } from "./validate";
import {
  OPENING_DAYS,
  type FactsField,
  type FactsItemField,
  type FactsPriceOption,
  type FactsSchema,
  type OpeningDay
} from "./types";

/**
 * What a facts form holds while somebody types, and how it becomes a set of values. Build round 18
 * (2026-10-07), for Petrovic's Fakta sheet.
 *
 * Moved here from `app/routes/adminClient/siteFactsInputs.ts` in build round 19 (2026-10-08), when
 * the client's Webbplatsens fakta began to use the same conversion. A client screen may not import
 * from the admin routes, whose code is kept out of the client's entry chunk (`adminChunks.ts`), and
 * one conversion in two copies would let the two forms send different values for the same typing.
 *
 * The form holds text, because an input holds text: "12,5" in a number field, "070-123 45 67" in a
 * phone field. `valuesFrom` turns that into what the gateway is sent, and it never repairs anything it
 * cannot read: a number field holding "tolv" is sent on as the string "tolv", so the browser's own
 * check (`validateFactsValues`) refuses it as `wrong_type` by the same rule the database would. The
 * one rewrite is the phone number, which the contract says the form normalises before sending.
 *
 * Empty is null throughout. An empty input, a list with no rows and a week of opening hours with no
 * time typed and no day closed all mean "no value", which is what the contract asks for rather than
 * an empty string.
 */

export interface DayInput {
  day: OpeningDay;
  closed: boolean;
  from: string;
  to: string;
}

export type ScalarInput = { kind: "scalar"; text: string };
export type PriceInput = { kind: "price"; amount: string; from: boolean; incl_vat: boolean };
export type ItemInput = ScalarInput | PriceInput;
export type ListRow = Record<string, ItemInput>;

export type FieldInput =
  | ScalarInput
  | PriceInput
  | { kind: "hours"; days: DayInput[] }
  | { kind: "list"; rows: ListRow[] };

export type FormInputs = Record<string, FieldInput>;

type Json = Record<string, unknown>;

function isObject(value: unknown): value is Json {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/** A stored number as a Swede types it: a comma before the decimals. */
function numberText(value: unknown): string {
  if (typeof value === "number") {
    return String(value).replace(".", ",");
  }
  // A string where a number belongs is shown as it is, so the form says what the draft holds.
  return typeof value === "string" ? value : "";
}

/** A fixed option is the schema's value whatever the stored one says; an editable one is stored. */
function optionValue(option: FactsPriceOption | undefined, stored: unknown): boolean {
  if (option && option.editable === false) {
    return option.value;
  }
  return stored === true;
}

export function emptyRow(fields: FactsItemField[]): ListRow {
  return Object.fromEntries(fields.map((field) => [field.key, itemInputFor(field, null)]));
}

function itemInputFor(field: FactsItemField, value: unknown): ItemInput {
  if (field.type === "price") {
    const price = isObject(value) ? value : {};
    return {
      kind: "price",
      amount: numberText(price.amount),
      from: optionValue(field.from, price.from),
      incl_vat: optionValue(field.incl_vat, price.incl_vat)
    };
  }
  if (field.type === "number") {
    return { kind: "scalar", text: numberText(value) };
  }
  return { kind: "scalar", text: typeof value === "string" ? value : "" };
}

function emptyWeek(): DayInput[] {
  return OPENING_DAYS.map((day) => ({ day, closed: false, from: "", to: "" }));
}

export function inputFor(field: FactsField, value: unknown): FieldInput {
  if (field.type === "opening_hours") {
    if (!Array.isArray(value)) {
      return { kind: "hours", days: emptyWeek() };
    }
    return {
      kind: "hours",
      days: OPENING_DAYS.map((day, index) => {
        const stored: unknown = value[index];
        const entry = isObject(stored) ? stored : {};
        return {
          day,
          closed: entry.closed === true,
          from: typeof entry.from === "string" ? entry.from : "",
          to: typeof entry.to === "string" ? entry.to : ""
        };
      })
    };
  }
  if (field.type === "list") {
    const items = field.item_fields ?? [];
    const rows = Array.isArray(value) ? value : [];
    return {
      kind: "list",
      rows: rows.map((row: unknown) => {
        const stored = isObject(row) ? row : {};
        return Object.fromEntries(items.map((item) => [item.key, itemInputFor(item, stored[item.key])]));
      })
    };
  }
  // Every other top-level type is one an item field can also have.
  return itemInputFor(field as FactsItemField, value);
}

export function inputsFrom(schema: FactsSchema, values: Json | null): FormInputs {
  return Object.fromEntries(schema.fields.map((field) => [field.key, inputFor(field, values?.[field.key] ?? null)]));
}

/** "12,5" and "1 495" as numbers. Anything else is handed on as typed, for the check to refuse. */
function readNumber(text: string): number | string | null {
  const compact = text.replace(/\s/g, "");
  if (compact === "") {
    return null;
  }
  const dotted = compact.replace(",", ".");
  return /^-?\d+(\.\d+)?$/.test(dotted) ? Number(dotted) : text;
}

function itemValue(field: FactsItemField, input: ItemInput | undefined): unknown {
  if (field.type === "price") {
    const price = input?.kind === "price" ? input : null;
    const amount = price ? readNumber(price.amount) : null;
    if (!price || amount === null) {
      return null;
    }
    return {
      amount,
      from: optionValue(field.from, price.from),
      incl_vat: optionValue(field.incl_vat, price.incl_vat)
    };
  }

  const text = input?.kind === "scalar" ? input.text : "";
  switch (field.type) {
    case "number":
      return readNumber(text);
    case "phone": {
      const trimmed = text.trim();
      return trimmed === "" ? null : (normalisePhone(trimmed) ?? trimmed);
    }
    default: {
      // Text, e-mail and address. Space at either end is never meant, and only spaces is no value.
      const trimmed = text.trim();
      return trimmed === "" ? null : trimmed;
    }
  }
}

export function fieldValue(field: FactsField, input: FieldInput | undefined): unknown {
  if (field.type === "opening_hours") {
    const days = input?.kind === "hours" ? input.days : emptyWeek();
    const untouched = days.every((day) => !day.closed && day.from === "" && day.to === "");
    if (untouched) {
      return null;
    }
    return days.map((day) =>
      day.closed
        ? { day: day.day, closed: true, from: null, to: null }
        : { day: day.day, closed: false, from: day.from === "" ? null : day.from, to: day.to === "" ? null : day.to }
    );
  }
  if (field.type === "list") {
    const rows = input?.kind === "list" ? input.rows : [];
    if (rows.length === 0) {
      return null;
    }
    const items = field.item_fields ?? [];
    return rows.map((row) => Object.fromEntries(items.map((item) => [item.key, itemValue(item, row[item.key])])));
  }
  return itemValue(field as FactsItemField, input?.kind === "scalar" || input?.kind === "price" ? input : undefined);
}

export function valuesFrom(schema: FactsSchema, inputs: FormInputs): Record<string, unknown> {
  return Object.fromEntries(schema.fields.map((field) => [field.key, fieldValue(field, inputs[field.key])]));
}

/** Only the keys `fields` name, each one present, a missing one as null. */
function pick(fields: Array<{ key: string }>, source: Json): Json {
  return Object.fromEntries(fields.map((field) => [field.key, source[field.key] ?? null]));
}

/**
 * One set of values as a string that is the same whenever the values are: the schema's keys only, a
 * missing key as null (in a list's rows too), an empty list as no list, and every object's keys in
 * order. Whether the form has unsaved changes, and whether a draft is the version already published,
 * is a comparison of two of these, which the order the database returns keys in cannot change.
 */
export function canonicalValues(schema: FactsSchema, values: unknown): string {
  const source = pick(schema.fields, isObject(values) ? values : {});
  for (const field of schema.fields) {
    const value = source[field.key];
    if (field.type === "list" && Array.isArray(value)) {
      source[field.key] =
        value.length === 0
          ? null
          : value.map((row: unknown) => (isObject(row) ? pick(field.item_fields ?? [], row) : row));
    }
  }
  return stableStringify(source);
}

/**
 * Whether two stored documents are the same document, the way Postgres compares two `jsonb` values:
 * every key and value equal, key order aside. `admin_publish_site_facts_version` refuses a draft
 * that is this to the latest version, so the screen says so before the press.
 */
export function sameDocument(left: unknown, right: unknown): boolean {
  return stableStringify(left) === stableStringify(right);
}

function stableStringify(value: unknown): string {
  if (Array.isArray(value)) {
    return `[${value.map(stableStringify).join(",")}]`;
  }
  if (isObject(value)) {
    const keys = Object.keys(value).sort();
    return `{${keys.map((key) => `${JSON.stringify(key)}:${stableStringify(value[key] ?? null)}`).join(",")}}`;
  }
  return JSON.stringify(value ?? null);
}
